import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { Image } from 'react-native'

import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { sectionizeInfiniteData } from '@/src/general/utils/sectionizeInfiniteData'

import { feedApi } from '../api/feedApi'

const SEPARATOR = ';'

export const feedOptions = (limit = 10, offset = 0) =>
  infiniteQueryOptions({
    queryKey: ['feed', limit, offset],
    queryFn: ({ signal, pageParam }) => feedApi.getFeed({ ...pageParam }, { signal }),
    initialPageParam: {
      limit,
      offset,
      timestamp: undefined as undefined | number,
    },
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.length === lastPageParam.limit
        ? // timestamp must be the lastPage item pushedAt property
          {
            offset: lastPageParam.offset + lastPageParam.limit,
            limit: lastPageParam.limit,
            timestamp: lastPage[lastPage.length - 1].pushedAt,
          }
        : null,
    retry: 0,
    select: (data) => {
      const flatData = data.pages.flat()
      return sectionizeInfiniteData(flatData, (item) => item.pushedAt)
    },
  })

export const useInfiniteFeed = (limit = 10, offset = 0) => {
  const query = useInfiniteQuery(feedOptions(limit, offset))

  /**
   * Stringified image sources to prefetch in a string list so that the useEffect does not rerun as long as the image sources are unchanged:
   * it would otherwise prefetch the same image multiple times on infinite loading
   */
  const imageSourceListToPrefetch = useMemo(() => {
    // Get only the first two sections of the feed
    const firstSection = query.data?.transformedSections[query.data?.mapSectionIndex[0]]
    const secondSection = query.data?.transformedSections[query.data?.mapSectionIndex[1]]
    const dataToPrefetch = [...(firstSection?.data || []), ...(secondSection?.data || [])]

    return dataToPrefetch.reduce(
      (acc, { publication }) => (publication ? [acc, getImageUrl(publication.picture)].join(SEPARATOR) : acc),
      '',
    )
  }, [query.data])

  useEffect(() => {
    if (imageSourceListToPrefetch) {
      const imageSources = imageSourceListToPrefetch.split(SEPARATOR)
      Promise.allSettled(imageSources.map((imageSource) => Image.prefetch(imageSource))).then((results) => {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.warn('❌ Failed to prefetch image:', imageSources[index])
          }
        })
      })
    }
  }, [imageSourceListToPrefetch])

  return query
}
